<template>
  <div>
    <p class="font-monospace" v-if="edit && localWorkoutData.id">
      <font-awesome-icon icon="fa-solid fa-fingerprint" />
      {{ localWorkoutData.id }}
    </p>

    <div>
      <div class="mb-4 position-relative">
        <div class="grid text-center" v-if="!edit && localWorkoutData.PublishedAt">
          <p class="lead" v-if="localWorkoutData.isPlanned">
            {{ $t('workout.plannedOn') }}
          </p>
          <h1 class="display-2">{{ formatDate(localWorkoutData.PublishedAt) }}</h1>
        </div>
        <span class="badge bg-dark" v-if="!edit && localWorkoutData.coach">
          {{ $t('workout.instructor') }}
        </span>
        <span class="badge bg-dark" v-else-if="!edit && localWorkoutData.workout.muscleGroups.format === 'group'">
          {{ $t('workout.format.group') }}
        </span>
        <div class="grid mb-3" v-if="edit">
          <label for="workout-date" class="form-label fw-bold mb-2">
            {{ $t('workout.dateLabel') }}
            <input
              id="workout-date"
              type="date"
              class="form-control form-control-lg mx-auto"
              style="max-width: 300px;"
              v-model="publishedAtInput"
              required /></label>
        </div>
      </div>
    </div>

    <FocusType
      v-if="localWorkoutData.workout.muscleGroups || edit"
      class="mb-4"
      v-model="localWorkoutData.workout.muscleGroups"
      v-model:isValid="isFocusValid"
      :edit="edit" />
    <CardioComponent
      v-if="localWorkoutData.workout.cardio || edit"
      class="mb-4"
      v-model="localWorkoutData.workout.cardio"
      v-model:isValid="isCardioValid"
      :edit="edit" />
    <ExercisesComponent
      v-if="localWorkoutData.workout.exercises || edit"
      class="mb-4"
      v-model="localWorkoutData.workout.exercises"
      v-model:isValid="isExercisesValid"
      :edit="edit"
      :totalWeight="localWorkoutData.workout.totalWeight" />
    <ReviewComponent
      v-if="(localWorkoutData.review || edit) && !localWorkoutData.isPlanned"
      class="mb-4"
      v-model="localWorkoutData.review"
      v-model:isValid="isReviewValid"
      :edit="edit" />
    <div class="mb-4" v-else>
      <span class="badge text-bg-dark">
        <font-awesome-icon icon="fa-solid fa-bell" />
        {{ $t('review.reminder') }}
      </span>
    </div>

    <div class="container mt-4 text-center" v-if="edit">
      <div class="btn-group gap-3" aria-label="label">
        <button type="button" class="btn btn-outline-dark" @click="$router.go($router.currentRoute)">
          <font-awesome-icon icon="fa-solid fa-angles-left" /> {{ $t('buttons.back') }}
        </button>
        <button type="button" class="btn btn-outline-dark" @click="handleSave" :disabled="!isFormValid">
          <font-awesome-icon icon="fa-solid fa-floppy-disk" /> {{ $t('buttons.submit') }}
        </button>

        <button
          type="button"
          class="btn btn-outline-danger"
          @click="handleDelete(localWorkoutData.id)"
          :disabled="!localWorkoutData.id">
          <font-awesome-icon icon="fa-solid fa-trash-can" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, reactive, computed, watch,
  onMounted,
} from 'vue';
import { openDB } from 'idb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useI18n } from 'vue-i18n';
import {
  updateWorkout,
  deleteWorkout,
  createWorkout,
} from '../../api/api';

import ExercisesComponent from './components/exercisesComponent.vue';
import ReviewComponent from './components/reviewComponent.vue';
import CardioComponent from './components/cardioComponent.vue';
import FocusType from './components/focusType.vue';

const DB_NAME = 'workout-drafts';
const STORE_NAME = 'drafts';
const dbPromise = openDB(DB_NAME, 1, {
  upgrade: (db) => db.createObjectStore(STORE_NAME),
});
const props = defineProps({
  workoutData: { type: Object, required: true },
  edit: { type: Boolean, default: false },
});

const { t } = useI18n();
const isReviewValid = ref(true);
const isCardioValid = ref(true);
const isFocusValid = ref(true);
const isExercisesValid = ref(true);
const localWorkoutData = reactive(JSON.parse(JSON.stringify(props.workoutData)));
const draftKey = computed(() => (props.workoutData.id ? `draft_${props.workoutData.id}` : 'draft_new'));

dayjs.extend(utc);
let debounceTimer;
watch(
  localWorkoutData,
  () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      const db = await dbPromise;
      await db.clear(STORE_NAME);
      await db.put(STORE_NAME, structuredClone(localWorkoutData), draftKey.value);
    }, 500);
  },
  { deep: true },
);
async function clearDraft() {
  const db = await dbPromise;
  await db.clear(STORE_NAME);
}
const publishedAtInput = computed({
  get() {
    return localWorkoutData.PublishedAt
      ? dayjs.utc(localWorkoutData.PublishedAt).format('YYYY-MM-DD')
      : '';
  },
  set(val) {
    localWorkoutData.PublishedAt = val
      ? dayjs.utc(val, 'YYYY-MM-DD').toISOString()
      : '';
  },
});

localWorkoutData.isPlanned = computed(() => {
  if (!localWorkoutData.PublishedAt) return false;
  return dayjs.utc(localWorkoutData.PublishedAt).isAfter(dayjs.utc());
});
function formatDate(date) {
  return dayjs.utc(date).format('DD.MM.YYYY');
}

const isFormValid = computed(() => (isReviewValid.value || !localWorkoutData.isPlanned)
  && isFocusValid.value
  && (isCardioValid.value || localWorkoutData.workout.cardio.length === 0)
  && (isExercisesValid.value || localWorkoutData.workout.exercises.length === 0));

async function handleSave() {
  if (localWorkoutData.isPlanned) {
    localWorkoutData.workout.review = {};
  }
  if (localWorkoutData.workout.cardio) {
    localWorkoutData.coach = (localWorkoutData.workout.muscleGroups.format === 'coach');
    localWorkoutData.workout.cardio = localWorkoutData.workout.cardio.map((exercise) => ({
      ...exercise,
      distance: Number.parseFloat(exercise.distance) || 0,
      speed: Number.parseFloat(exercise.speed) || 0,
      time: Number.parseFloat(exercise.time) || 0,
      calories: Number.parseFloat(exercise.calories) || 0,
      heart: Number.parseFloat(exercise.heart) || 0,
    }));
  }

  try {
    const payload = localWorkoutData;
    if (payload.id) {
      await updateWorkout(payload);
      globalThis.$toast?.showToast(t('info.updated'), 'success');
    } else {
      await createWorkout(payload);
      window.location.reload();
      globalThis.$toast?.showToast(t('info.created'), 'success');
    }
    await clearDraft();
  } catch (error) {
    console.log(error);
    globalThis.$toast?.showToast(error?.response?.data?.error || 'Error', 'danger');
  }
}

async function handleDelete(id) {
  try {
    await deleteWorkout(id);
    globalThis.$toast?.showToast(t('info.deleted'), 'success');
    await clearDraft();
    window.location.reload();
  } catch (error) {
    globalThis.$toast?.showToast(error?.response?.data?.error || 'Error', 'danger');
  }
}
onMounted(async () => {
  if (props.edit) window.scrollTo(0, 0);
  const db = await dbPromise;
  const draft = await db.get(STORE_NAME, draftKey.value);
  if (draft) Object.assign(localWorkoutData, draft);
});

</script>
